/**
 * Add Team Dialog
 * Form to create new team with validation
 * Based on database schema: teams table
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEligibleTeamLeads } from '@/lib/hooks/useEmployees';
import { Loader2Icon } from 'lucide-react';

interface AddTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TeamFormData) => void;
}

export interface TeamFormData {
  name: string;
  leadId?: string;
}

export function AddTeamDialog({ open, onOpenChange, onSubmit }: AddTeamDialogProps) {
  const [formData, setFormData] = useState<TeamFormData>({
    name: '',
    leadId: undefined,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof TeamFormData, string>>>({});

  // Fetch eligible team leads from API
  const { data: eligibleLeads = [], isLoading: isLoadingLeads } = useEligibleTeamLeads();

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TeamFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Team name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      onSubmit(formData);
      // Reset form
      setFormData({ name: '', leadId: undefined });
      setErrors({});
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', leadId: undefined });
    setErrors({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Team</DialogTitle>
          <DialogDescription>
            Add a new team to your organization. Team lead is optional and can be assigned later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Team Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Team Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Engineering, Sales, Operations, etc."
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          {/* Team Lead */}
          <div className="space-y-2">
            <Label htmlFor="lead">Team Lead (Optional)</Label>
            <Select
              value={formData.leadId || 'none'}
              onValueChange={(value) =>
                setFormData({ ...formData, leadId: value === 'none' ? undefined : value })
              }
              disabled={isLoadingLeads}
            >
              <SelectTrigger>
                <SelectValue placeholder={isLoadingLeads ? 'Loading...' : 'Select a team lead'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Team Lead</SelectItem>
                {isLoadingLeads ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  eligibleLeads.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name} ({employee.role.replace('_', ' ')})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Only employees with Team Lead, Manager, or Admin roles can be assigned as team leads.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit">Create Team</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
